import React, { useEffect, useState } from 'react';
import { Box, Card, CardHeader, CardContent, Typography } from '@mui/material';
import {
  Timeline as TimelineIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Calculate as CalculateIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../../store';
import { TraceEvent, ActionFeedItem } from '../../types';

interface TimelineItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  isSuccess?: boolean;
}


const TimelineItem: React.FC<TimelineItemProps> = ({
  icon,
  title,
  description,
  isSuccess = false,
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        marginBottom: '16px',
        paddingLeft: '20px',
        '&:last-child': { marginBottom: 0 },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '6px',
          top: 0,
          bottom: '-16px',
          width: '2px',
          backgroundColor: '#e0e0e0',
        },
        '&:last-child::before': {
          bottom: 0,
        },
      }}
    >

      <Box
        sx={{
          position: 'absolute',
          left: '-14px',
          top: '4px',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: isSuccess ? 'success.main' : 'primary.main',
          border: '2px solid #fff',
          boxShadow: `0 0 0 2px ${isSuccess ? '#27ae60' : '#667eea'}`,
          zIndex: 1,
        }}
      />


      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
        <Box sx={{ color: 'secondary.main', display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#1a1a2e',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: '11px',
          color: 'text.secondary',
          lineHeight: 1.4,
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

const parseTraceEvent = (event: TraceEvent): ActionFeedItem | null => {
  const msg = event.message || '';
  const node = event.node || 'system';
  const etype = event.event_type || 'progress';

  if (msg.includes('Starting node') || msg.includes('Finished node')) {
    return null;
  }
  if (etype === 'start' && node === 'system') {
    return null;
  }

  let item: ActionFeedItem = {
    icon: 'robot',
    title: 'System Event',
    body: msg,
    style: 'info',
  };

  const msgLower = msg.toLowerCase();

  if (node === 'system' || node === 'watchdog') {
    item.icon = 'sitemap';
    item.title = 'Orchestrator';
    
    if (msgLower.includes('complete') || msgLower.includes('summary') || msgLower.includes('finished')) {
      item.icon = 'description';
      item.title = 'Insight Compiler';
      item.style = 'success';
    }
  }

  if (node === 'analyst') {
    item.icon = 'search';
    item.title = 'Discovery';
    
    if (msgLower.includes('policy')) {
      item.title = 'Policy Insight';
      item.style = 'success';
    } else if (msgLower.includes('constraint') || msgLower.includes('capacity')) {
      item.title = 'Constraint Discovery';
      item.style = 'warning';
    } else if (msgLower.includes('tool call')) {
      item.title = 'Tool Execution';
      const tool = msg.replace('Tool call: ', '').split('(')[0];
      item.body = `Running tool \`${tool}\`...`;
    }
  }

  if (node === 'optimizer' || node === 'solver') {
    item.icon = 'calculate';
    item.title = 'Optimization';
    
    if (msgLower.includes('allocated')) {
      item.title = 'Calculation Complete';
      item.style = 'success';
    } else if (msgLower.includes('protecting') || msgLower.includes('priority')) {
      item.title = 'Strategy Applied';
      item.style = 'success';
    }
  }

  if (node === 'narrator') {
    item.icon = 'description';
    item.title = 'Narration';
  }

  if (etype === 'error') {
    item.icon = 'error';
    item.title = 'Error';
    item.style = 'error';
  }

  return item;
};

const getIconComponent = (iconName: string): React.ReactNode => {
  const iconProps = { sx: { fontSize: 14, color: 'secondary.main' } };
  
  switch (iconName) {
    case 'search':
      return <SearchIcon {...iconProps} />;
    case 'trending_up':
      return <TrendingUpIcon {...iconProps} />;
    case 'analytics':
      return <AnalyticsIcon {...iconProps} />;
    case 'calculate':
      return <CalculateIcon {...iconProps} />;
    case 'description':
      return <DescriptionIcon {...iconProps} />;
    default:
      return <SearchIcon {...iconProps} />;
  }
};

const ActionFeed: React.FC = () => {
  const { traces, isRunning } = useAppSelector((state) => state.analysis);

  const feedItems = traces
    .map(parseTraceEvent)
    .filter((item): item is ActionFeedItem => item !== null);

  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (!isRunning) {
      setVisibleCount(feedItems.length);
      return;
    }

    if (feedItems.length === 0) {
      setVisibleCount(0);
      return;
    }

    if (visibleCount > feedItems.length) {
      setVisibleCount(feedItems.length);
      return;
    }

    if (visibleCount >= feedItems.length) {
      return;
    }

    const timer = window.setTimeout(() => {
      setVisibleCount((prev) => Math.min(feedItems.length, prev + 1));
    }, 180);

    return () => window.clearTimeout(timer);
  }, [feedItems.length, isRunning, visibleCount]);

  const visibleFeedItems = feedItems.slice(0, visibleCount);

  return (
    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>
              Action Feed
            </Typography>
          </Box>
        }
        sx={{ borderBottom: 'none', paddingBottom: 0 }}
      />
      <CardContent
        sx={{
          paddingTop: '10px',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {visibleFeedItems.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {isRunning ? 'Waiting for activity...' : 'Click Analyze to see agent activity'}
          </Typography>
        ) : (
          <Box sx={{ position: 'relative', paddingLeft: '20px' }}>
            <Box
              sx={{
                position: 'absolute',
                left: '6px',
                top: 0,
                bottom: 0,
                width: '2px',
                backgroundColor: '#e0e0e0',
              }}
            />
            
            {visibleFeedItems.map((item, index) => (
              <TimelineItem
                key={index}
                icon={getIconComponent(item.icon)}
                title={item.title}
                description={item.body}
                isSuccess={item.style === 'success'}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActionFeed;
